import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { LanguageShortcutName, Translation } from '../../Models/Models';
import { FieldLengths } from '../../Models/FieldLengths';
import { SiteContext, ToastContext } from '../../Contexts';
import { Utils } from '../../Services/Utils';
import { quillModules } from '../../Models/quillModules';
import { TextService } from '../../Services/TextService';
import { EntityColors } from '../../Themes/EntityColors';
import DiscardButton from '../Buttons/DiscardButton';
import SaveButton from '../Buttons/SaveButton';
import DeleteButton from '../Buttons/DeleteButton';
import EditButton from '../Buttons/EditButton';
import FormShell from '../Form/FormShell';
import CharCount from '../Form/CharCount';
import { useIsDirty } from '../../Hooks/useIsDirty';
import { FlagImage, AddLanguageTile, LanguagePickerModal } from '../Language/LanguagePicker';

const TextEditComponent: React.FC = () => {
    useEffect(() => {
        Utils.setDocumentTitle('Úprava textu');
    }, []);

    const { getText, updateText } = TextService();
    const { currentSite }: any = useContext(SiteContext);
    const { setToast }: any = useContext(ToastContext);
    const [translations, setTranslations] = useState<Translation[]>([]);
    const [pageId, setPageId] = useState<number | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [showAddLanguageModal, setShowAddLanguageModal] = useState(false);
    const [showChangeLanguageModal, setShowChangeLanguageModal] = useState(false);
    const isDirty = useIsDirty(translations, loaded);

    const { textId } = useParams<{ textId: string }>();
    const textEntityId = Number(textId);
    const { action, icon } = EntityColors.text;

    const languageShortCutNames = useMemo<readonly LanguageShortcutName[]>(() =>
        (Object.values(LanguageShortcutName) as LanguageShortcutName[])
            .map(short => Utils.capitalizeFirstLetter(short) as LanguageShortcutName),
        []
    );

    useEffect(() => {
        const load = async () => {
            const resp = await getText(Number(textId));
            if (resp) {
                const sorted = [...resp.translations].sort((a, b) =>
                    a.isDefault === b.isDefault ? 0 : a.isDefault ? -1 : 1
                );
                setTranslations(sorted);
                setPageId(resp.pageId);
            }
            setLoaded(true);
        };
        void load();
    }, [textId]);

    const doSave = async (): Promise<boolean | void> => {
        // The editor has no maxLength: its HTML (tags included) is what the 10 000-character column stores.
        const tooLong = translations.find(t => t.value.length > FieldLengths.translationValue);
        if (tooLong) {
            setToast({
                message: `Text (${Utils.capitalizeFirstLetter(tooLong.languageShortcut)}) je príliš dlhý: `
                    + `${tooLong.value.length.toLocaleString('sk-SK')} / ${FieldLengths.translationValue.toLocaleString('sk-SK')} znakov vrátane formátovania.`,
            });
            return false;
        }

        await updateText({ TextId: textEntityId, Translations: translations }, 1);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        void doSave();
    };

    const handleLanguageSelect = (short: LanguageShortcutName) => {
        if (!translations.some(t => t.languageShortcut === short)) {
            setTranslations(prev => [
                ...prev,
                { value: '', languageShortcut: short, textId: textEntityId, isDefault: false },
            ]);
        }
        setShowAddLanguageModal(false);
    };

    const handleDefaultLanguageChange = (short: LanguageShortcutName) => {
        const existing = translations.filter(t => t.languageShortcut !== short);
        const picked = translations.find(t => t.languageShortcut === short) ?? {
            value: '', languageShortcut: short, textId: textEntityId, isDefault: false,
        } as Translation;
        setTranslations([{ ...picked, isDefault: true }, ...existing.map(t => ({ ...t, isDefault: false }))]);
        setShowChangeLanguageModal(false);
    };

    const handleTextChange = (langShortcut: LanguageShortcutName, val: string) => {
        setTranslations(prev => prev.map(t => (t.languageShortcut === langShortcut ? { ...t, value: val } : t)));
    };

    const handleRemoveLanguage = (langShortcut: LanguageShortcutName) => {
        setTranslations(prev => prev.filter(t => t.languageShortcut !== langShortcut));
    };

    const addOptions = languageShortCutNames
        .filter(short => !translations.some(t => t.languageShortcut === short));

    const changeDefaultOptions = languageShortCutNames
        .filter(short => !translations.some(t => t.isDefault && t.languageShortcut === short));

    return (
        <>
            <FormShell title="Úprava textu" icon={icon} accentColor={action} backHref={`/text/table/${pageId}`} isDirty={isDirty}>
                {!loaded ? (
                    <p className="text-center py-4 text-secondary">Načítavam...</p>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <div className="st-trans-stack">
                            {translations.length === 0 && (
                                <p className="st-trans-empty">
                                    Tento text zatiaľ nemá žiadne preklady. Pridajte prvý jazyk.
                                </p>
                            )}

                            {translations.map(tr => (
                                <div
                                    key={tr.languageShortcut}
                                    className={`st-trans-card${tr.isDefault ? ' st-trans-card--default' : ''}`}
                                    style={{ '--trans-accent': action } as React.CSSProperties}
                                >
                                    <div className="st-trans-card__head">
                                        <div className="st-trans-card__id">
                                            <FlagImage short={tr.languageShortcut} size="sm" />
                                            <span className="st-trans-card__lang">
                                                {Utils.capitalizeFirstLetter(tr.languageShortcut)}
                                            </span>
                                            {tr.isDefault && (
                                                <span className="st-trans-card__badge">
                                                    Predvolený preklad
                                                </span>
                                            )}
                                        </div>
                                        {tr.isDefault ? (
                                            <EditButton color="gray" text="Zmeniť" onClick={() => setShowChangeLanguageModal(true)} />
                                        ) : (
                                            <DeleteButton iconOnly onClick={() => handleRemoveLanguage(tr.languageShortcut)} />
                                        )}
                                    </div>
                                    <div className="text-edit-quill st-trans-card__body">
                                        <ReactQuill
                                            modules={quillModules}
                                            value={tr.value}
                                            onChange={text => handleTextChange(tr.languageShortcut, text)}
                                        />
                                        <div className="st-field-foot">
                                            <CharCount value={tr.value} max={FieldLengths.translationValue} note="vrátane formátovania" />
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <AddLanguageTile accentColor={action} onClick={() => setShowAddLanguageModal(true)} />
                        </div>

                        <div className="d-flex justify-content-center gap-3 mt-4">
                            <DiscardButton to={"/text/table/"+pageId} isDirty={isDirty} />
                            <SaveButton type="submit" onSave={doSave} websiteId={currentSite.id} />
                        </div>
                    </form>
                )}
            </FormShell>

            <LanguagePickerModal
                show={showAddLanguageModal}
                title="Vyberte jazyk"
                accentColor={action}
                options={addOptions}
                onHide={() => setShowAddLanguageModal(false)}
                onPick={handleLanguageSelect}
            />

            <LanguagePickerModal
                show={showChangeLanguageModal}
                title="Vyberte predvolený jazyk"
                accentColor={action}
                options={changeDefaultOptions}
                onHide={() => setShowChangeLanguageModal(false)}
                onPick={handleDefaultLanguageChange}
            />
        </>
    );
};

export default TextEditComponent;
